import React from 'react'
import { useNavigate } from 'react-router-dom';
import { useTable, useFilters, useGlobalFilter, useAsyncDebounce, useSortBy, usePagination } from 'react-table'
import { ChevronDoubleLeftIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline'

import { Button, PageButton } from '../shared/Button'
import { classNames } from '../shared/Utils'
import { SortIcon, SortUpIcon, SortDownIcon } from '../shared/Icons'
import AddProjectForm from '../Modal/AddProjectForm';
// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length
  const [value, setValue] = React.useState(globalFilter)
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined)
  }, 200)

  return (
    <label className="flex gap-x-2 items-baseline">
      <span className="text-gray-700">搜索: </span>
      <input
        type="text"
        className="rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
        value={value || ""}
        onChange={e => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder=""
      />
    </label>
  )
}

const filters = {
  housingType: [
    { value: 'Y2', label: 'Y2型', checked: false },
    { value: 'Y3', label: 'Y3型', checked: false },
    { value: 'Y4', label: 'Y4型', checked: false },
    { value: 'NCB', label: '新十字型', checked: false },
    { value: 'CB', label: '康和式', checked: false },
    { value: 'HR', label: '和諧式', checked: false },
    { value: 'LB', label: '相連長型', checked: false },
    { value: 'OS', label: '長型或新長型', checked: false },
    { value: 'SORDHB', label: '單或雙工字型', checked: false },
    { value: 'TT', label: '雙塔式', checked: false },
    { value: 'NSBD', label: '非標準型', checked: false },
    { value: 'Others', label: '其他', checked: false },
  ],
  buildingType: [
    { value: 'Public', label: '公屋', checked: false },
    { value: 'Estate', label: '居屋', checked: false },
    { value: 'Private', label: '私樓', checked: false },
    { value: 'Others', label: '其他', checked: false },
  ]
}
// This is a custom filter UI for selecting
// a unique option from a list
export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id, render },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set()
    preFilteredRows.forEach(row => {
      options.add(row.values[id])

    })
    return [...options.values()]
  }, [id, preFilteredRows])

  // Render a multi-select box
  return (
    <label className="flex gap-x-2 items-baseline">
      <span className="text-gray-700">{render("Header")}: </span>
      <select
        className="rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
        name={id}
        id={id}
        value={filterValue}
        onChange={e => {
          setFilter(e.target.value || undefined)
        }}
      >
        <option value="">全部</option>
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option == 1 ? "精選" : "非精選"}
          </option>
        ))}
      </select>
    </label>
  )
}

export function StatusPill({ value }) {
  const status = value ? value.toLowerCase() : "unknown";

  return (
    <span
      className={
        classNames(
          "px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm",
          status.startsWith("active") ? "bg-green-100 text-green-800" : null,
          status.startsWith("inactive") ? "bg-yellow-100 text-yellow-800" : null,
          status.startsWith("offline") ? "bg-red-100 text-red-800" : null,
        )
      }
    >
      {status}
    </span>
  );
};

export function formatDate({ value }) {
  const date = new Date(value).toLocaleDateString(("es-CL"));
  return (
    <span
      className={
        classNames(
          "text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
        )
      }
    >
      {date}
    </span>
  );
};

export function hasFeatured({ value }) {

  return (
    <span
      className={
        classNames(
          "text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
        )
      }
    >
      {value == 1 ? <svg aria-hidden="true" className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><title>First star</title><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
        : <svg aria-hidden="true" className="w-5 h-5 text-gray-300 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><title>Fifth star</title><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>}
    </span>
  );
};

export function projectBuildingType({ value }) {

  return (
    <span
      className={
        classNames(
          "text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
        )
      }
    >
      {filters.buildingType.map((option, optionIdx) => (
        (option.value == value) ? option.label : ""
      ))}
      
    </span>
  );
};

export function projectBuildingStyle({ value }) {

  return (
    <span
      className={
        classNames(
          "text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
        )
      }
    >
     {filters.housingType.map((option, optionIdx) => (
        (option.value == value) ? option.label : ""
      ))}
    </span>
  );
};

function Edit(value) {
  // console.log("edit ===== " + value);

}

async function hideProject(projectId) {
  var confirmHideProject = window.confirm('你確定隱藏這個項目嗎?');

  if (confirmHideProject) {

    const param = { "projectId": parseInt(projectId) };
    // console.log(JSON.stringify(param))
    const token = localStorage.getItem("token");
    await fetch(process.env.REACT_APP_API_BASE_LINK + '/v1/fookchong/cms/hideProject', {
      method: "POST",
      body: JSON.stringify(param),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
    }).then((response) => response.json())
      .then((data) => {
        alert("隱藏成功!")
        window.location.reload();
      })
  }
}

async function showProject(projectId) {
  var confirmShowProject = window.confirm('你確定顯示這個項目嗎?');

  if (confirmShowProject) {

    const param = { "projectId": parseInt(projectId) };
    // console.log(JSON.stringify(param))
    const token = localStorage.getItem("token");
    await fetch(process.env.REACT_APP_API_BASE_LINK + '/v1/fookchong/cms/showProject', {
      method: "POST",
      body: JSON.stringify(param),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
    }).then((response) => response.json())
      .then((data) => {
        alert("顯示成功!")
        window.location.reload();
      })
  }
}

export function Action({ value }) {

  const [showModal, setShowModal] = React.useState(false);
  var str = value.split(" ");
  return (
    <span
      className={
        classNames(
          "text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
        )
      }
    >
      <div className="flex">
        <a href={"/cms/admin/project/projectDetail/" + str[0]} onClick={() => Edit(str[0])} className="w-4 mr-2 focus:outline-none transform hover:text-purple-500 hover:scale-110">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </a>
        {
          (str[1] == 1 || str[1] == "null")
            ?
            <a onClick={() => hideProject(str[0])} className="w-4 mr-2 focus:outline-none transform hover:text-purple-500 hover:scale-110">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><g id="SVGRepo_bgCarrier" strokeWidth="2"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path strokeWidth="2" d="M2,12S5,4,12,4s10,8,10,8-2,8-10,8S2,12,2,12Z"></path><circle cx="12" cy="12" r="4"></circle></g></svg>
            </a>
            :
            <a onClick={() => showProject(str[0])} className="w-4 mr-2 focus:outline-none transform hover:text-purple-500 hover:scale-110">
              <svg stroke="currentColor" fill="#000000" viewBox="0 0 14 14" role="img" focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="m 11.050789,4.6807995 c 0.812763,0.612897 1.482232,1.405696 1.949211,2.3192 -1.114655,2.180489 -3.3830004,3.6732525 -6,3.6732525 -0.5944749,0 -1.1709588,-0.07703 -1.7200155,-0.221649 L 6.365699,9.3658875 c 0.2023064,0.0541 0.4149352,0.08295 0.634301,0.08295 1.3524539,0 2.4488345,-1.0963809 2.4488345,-2.448835 0,-0.219365 -0.028841,-0.431994 -0.082946,-0.634301 L 11.050789,4.6807995 Z M 8.7200154,3.5483969 C 8.1709586,3.4037765 7.5944748,3.3267482 7,3.3267482 c -2.6169992,0 -4.885345,1.4927633 -5.9999999,3.6732513 0.4669791,0.913505 1.1364481,1.706304 1.9492117,2.319201 l 1.6848998,-1.6849 c -0.054102,-0.202306 -0.082946,-0.414935 -0.082946,-0.634301 0,-1.352454 1.0963805,-2.448834 2.4488344,-2.448834 0.219366,0 0.4319946,0.02884 0.6343008,0.08295 L 8.7200154,3.5483969 Z M 10.896072,2.2381344 11.761866,3.1039281 3.1039283,11.761866 2.2381346,10.896072 10.896072,2.2381344 Z"></path></g></svg>
            </a>

        }

        {showModal ? (
          <>
            <div
              className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
            >
              <div className="relative w-auto ">
                {/*content*/}
                <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">


                  {/*body*/}
                  <div className="px-8 mt-8">

                    <div className="mt-3 sm:flex">
                      <div className="flex items-center justify-center flex-none w-12 h-12 mx-auto bg-red-100 rounded-full">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-6 h-6 text-red-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="mt-2 text-center sm:ml-4 sm:text-left">
                        <h5 className="text-lg font-medium text-gray-800">
                          這個動作不能撤消。 您確定刪除這個項目嗎？
                        </h5>

                      </div>
                    </div>
                  </div>
                  {/*footer*/}
                  <div className="flex items-center justify-end p-6 rounded-b">
                    <button
                      className="w-full m-2 p-2.5 flex-1 text-white bg-red-600 rounded-md outline-none ring-offset-2 ring-red-600 hover:bg-red-800 focus:outline-none"

                    >
                      刪除
                    </button>
                    <button
                      className="w-full m-2 p-2.5 flex-1 text-gray-800 rounded-md outline-none border ring-offset-2 ring-indigo-600 hover:bg-gray-300 focus:outline-none"
                      onClick={() =>
                        setShowModal(false)
                      }
                    >
                      取消
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
          </>
        ) : null}
      </div>
    </span>
  );
};

function Table({ columns, data }) {
  // Use the state and functions returned from useTable to build your UI

  let navigate = useNavigate();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable({
    columns,
    data,
  },
    useFilters, // useFilters!
    useGlobalFilter,
    useSortBy,
    usePagination,  // new
  )

  // Render the UI for your table
  return (
    <>
      <div className="sm:flex sm:gap-x-3 mt-10">
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) =>
            column.Filter ? (
              <div className="mt-2 mb-4 sm:mt-0" key={column.id}>
                {column.render("Filter")}
              </div>
            ) : null
          )
        )}


        <div className=" max-w-full flex-grow flex-1 lg:text-right md:text-right">
          <AddProjectForm />
        </div>

      </div>
      {/* table */}
      <div className="mt-4 flex flex-col">
        <div className="-my-2 overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(column => (
                        // Add the sorting props to control sorting. For this example
                        // we can add them into the header props
                        <th
                          scope="col"
                          className="group px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                        >
                          <div className="flex items-center justify-between">
                            {column.render('Header')}
                            {/* Add a sort direction indicator */}
                            <span>
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? <SortDownIcon className="w-4 h-4 text-gray-400" />
                                  : <SortUpIcon className="w-4 h-4 text-gray-400" />
                                : (
                                  <SortIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
                                )}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className="bg-white divide-y divide-gray-200"
                >
                  {page.map((row, i) => {  // new
                    prepareRow(row)
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map(cell => {
                          return (
                            <td
                              {...cell.getCellProps()}
                              className="px-6 py-4 whitespace-nowrap"
                              role="cell"
                            >
                              {cell.column.Cell.name === "defaultRenderer"
                                ? <div className="text-sm text-gray-500">{cell.render('Cell')}</div>
                                : cell.render('Cell')
                              }
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* Pagination */}
      <div className="py-3 flex items-center justify-between">

        <div className="sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="flex gap-x-2 items-baseline">
            <span className="text-sm text-gray-700">
              Page <span className="font-medium">{state.pageIndex + 1}</span> of <span className="font-medium">{pageOptions.length}</span>
            </span>
            <label>
              <span className="sr-only">Items Per Page</span>

            </label>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <PageButton
                className="focus:outline-none rounded-l-md"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                <span className="sr-only">First</span>
                <ChevronDoubleLeftIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </PageButton>
              <PageButton
                className="focus:outline-none rounded-r-md"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </PageButton>
              <PageButton
                className="focus:outline-none rounded-r-md"
                onClick={() => nextPage()}
                disabled={!canNextPage
                }>
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </PageButton>
              <PageButton
                className="focus:outline-none rounded-r-md"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                <span className="sr-only">Last</span>
                <ChevronDoubleRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </PageButton>
            </nav>
          </div>
        </div>
      </div>
    </>
  )
}

export default Table;